/// <reference types="i18n" />
import {TranslationKey} from './TranslationKeyTypes'
import {Loggable} from './types'

export type InternalErrorKey = 'internalErrorMessage'

type translate = typeof i18n.__

export type UserFacingErrorIdentifier = TranslationKey
export type ErrorLoggableType = string | number | boolean | null | undefined
export type ErrorLogData = Record<string, ErrorLoggableType>
export type ErrorSeverity = 'error' | 'warning' | 'log'
export interface CollabValidationUpdateInfo {
  url?: string
  force: boolean
}
export enum CollabErrorCode {
  InvalidSiteId,
  PermissionDenied,
  NonexistentNote,
  InitializationError,
  ValidationError,
  FingerprintFetchFailed,
  NoteIsReadonly,
  InvalidCredentials,
  ServerDisconnect,
  UploadInProgress,
  LameDuckServerShutdown
}

export interface ICollabError {
  // used to carry additional error context to the client
  data: {
    code: CollabErrorCode
    context: Record<string, any> | undefined
    forceLogOut?: boolean
  }
}

export function isCollabError(error: unknown): error is ICollabError {
  if (typeof error !== 'object' || error === null) {
    return false
  }
  const collabError = error as ICollabError
  return collabError?.data?.code !== undefined && collabError.data?.context !== undefined
}

type UserErrorLog = {userError: {key: UserFacingErrorIdentifier; stack?: string}}
type CompoundUserErrorLog = {compoundUserError: {key: UserFacingErrorIdentifier; children: ErrorID[]; stack?: string}}
export type InternalErrorLog = {internalError: {incidentID: string; error: any}}
type LogObj = UserErrorLog | CompoundUserErrorLog | InternalErrorLog

export type UserFacingErrorContainer = {
  key: UserFacingErrorIdentifier
  args: Record<string, string>
}

export type ErrorID = UserFacingErrorContainer | UserFacingErrorIdentifier
const extractKeyHelper = (eid: ErrorID): UserFacingErrorIdentifier => (typeof eid === 'string' ? eid : eid.key)
const translateKeyHelper = (eid: ErrorID, t: translate): string =>
  typeof eid === 'string' ? t(eid) : t(eid.key, eid.args)

/**
 * The shape of errors that will be transmitted to clients.
 */
export interface ErrorForClient {
  message: string
  key: UserFacingErrorIdentifier | InternalErrorKey
  forceLogOut: boolean
  retryAfter?: string
  updateInfo?: CollabValidationUpdateInfo
  statusCode?: number
}
export function isErrorForClient(err: unknown): err is ErrorForClient {
  return (
    typeof err === 'object' &&
    err !== null &&
    'message' in err &&
    typeof err.message === 'string' &&
    'key' in err &&
    typeof err.key === 'string' &&
    'forceLogOut' in err &&
    err.forceLogOut !== undefined
  )
}

/**
 * Abstract base class for a user facing error
 * This *SHOULD NOT* be initalized directly
 *
 * @extends Error
 */
export abstract class AbstractBaseUserFacingError extends Error {
  key: UserFacingErrorIdentifier
  forceLogOut: boolean
  retryAfter?: string
  updateInfo?: CollabValidationUpdateInfo
  statusCode: number
  constructor(
    key: UserFacingErrorIdentifier,
    forceLogOut = false,
    retryAfter?: string,
    statusCode: number = 500,
    updateInfo?: CollabValidationUpdateInfo
  ) {
    super()
    this.key = key
    this.forceLogOut = forceLogOut
    this.retryAfter = retryAfter
    this.statusCode = statusCode
    this.updateInfo = updateInfo
  }

  /**
   * Handles generating the message to return to the user
   *
   * @param t The translation function to use
   * @returns The translated message to return to the client and be displayed to the user
   */
  abstract messageToReturn(t: translate): string

  /**
   * Generates a message to log to the console with additional context for the error
   *
   * @returns A message containing the serialized error to log to the console
   */
  abstract messageToLog(): LogObj
}

/**
 * Throws an error if in local dev, logs an error if in production.
 *
 * @param condition If this resolves false, the assert will fire.
 * @param message Description of the error helpful toward fixing it.
 */
export function nonFatalAssert(condition: boolean, loggable: string | Loggable) {
  if (condition) {
    return
  }

  const message = typeof loggable === 'string' ? loggable : JSON.stringify(loggable)
  if (process.env.NODE_ENV === 'production') {
    console.error(message)
  } else {
    throw new Error(message)
  }
}

export interface UserFacingErrorParams {
  forceLogOut?: boolean
  retryAfter?: string
  logData?: ErrorLogData
  statusCode?: number | undefined
  severity?: ErrorSeverity
  updateInfo?: CollabValidationUpdateInfo
}

/**
 * Represents an error which we want to show to a user clientside
 * @extends AbstractBaseUserFacingError
 */
export class UserFacingError extends AbstractBaseUserFacingError {
  private id: ErrorID
  private logData: ErrorLogData
  public severity: ErrorSeverity

  constructor(key: ErrorID, opts?: UserFacingErrorParams) {
    super(extractKeyHelper(key), opts?.forceLogOut, opts?.retryAfter, opts?.statusCode, opts?.updateInfo)
    this.id = key
    this.logData = opts?.logData ?? {}
    this.message = extractKeyHelper(key)
    this.severity = opts?.severity ?? 'error'
  }

  messageToReturn = (t: translate) => {
    return translateKeyHelper(this.id, t)
  }
  messageToLog = () => ({userError: {key: this.key, stack: this.stack}, ...this.logData})
  getLogData = () => this.logData
  getUpdateInfo = () => this.updateInfo
}
// Instead of needing a translated string, uses an arbitrary string so we can be more expressive.
// Do not use on non-admin pages
export class AdminFacingError extends AbstractBaseUserFacingError {
  private logData: ErrorLogData

  public severity: ErrorSeverity

  constructor(
    readonly message: string,
    opts?: UserFacingErrorParams
  ) {
    super('admin', opts?.forceLogOut, opts?.retryAfter, opts?.statusCode, opts?.updateInfo)

    this.logData = opts?.logData ?? {}
    this.severity = opts?.severity ?? 'error'
  }

  messageToReturn = (t: translate) => {
    return this.message
  }
  messageToLog = () => ({userError: {key: this.key, stack: this.stack}, ...this.logData})
  getLogData = () => this.logData
  getUpdateInfo = () => this.updateInfo
}

export function errorToUserFacingError(error: Error, defaultMessage: TranslationKey) {
  if (error instanceof UserFacingError) {
    return error
  } else {
    return new UserFacingError(defaultMessage, {
      logData: {
        message: error.message,
        stack: error.stack,
        name: error.name
      }
    })
  }
}

export function isUserFacingError(error: unknown): error is UserFacingError {
  return error instanceof UserFacingError
}

export function assert(condition: boolean, key: TranslationKey): asserts condition is true {
  if (!condition) {
    throw new UserFacingError(key)
  }
}

/**
 * User Facing Error which is made up of multiple {@Link ErrorID}'s
 *
 * You may want to use this error if your error message is assembeled dynamically of different {@Link ErrorID}'s
 * ```
 * const errors = []
 * if (property1) errors.push('errorKey1')
 * if (property2) errors.push('errorKey2')
 * if (property3) errors.push('errorKey3')
 * throw new CompoundUserFacingError(errors)
 * ```
 * In this example we conditionally add keyed user facing errors. The ones that get added will be joined into one string with a period separating each error.
 *
 * @extends AbstractBaseUserFacingError
 */
export class CompoundUserFacingError extends AbstractBaseUserFacingError {
  private keys: ErrorID[]
  constructor(key: UserFacingErrorIdentifier, errors: ErrorID[], forceLogOut = false, retryAfter?: string) {
    super(key, forceLogOut)
    this.keys = errors
    this.message = key
    this.retryAfter = retryAfter
  }

  messageToLog = () => ({compoundUserError: {key: this.key, children: this.keys, stack: this.stack}})

  messageToReturn = (t: translate) => {
    const translatedMessages = this.keys.map((key) => translateKeyHelper(key, t))
    return translatedMessages.length > 1 ? translatedMessages.join('. ') : translatedMessages.join()
  }
}

/**
 * The follow are explicit cases of UserFacingError to standardize messaging and status codes
 */
export class BadRequestError extends UserFacingError {
  constructor(t: ErrorID = 'badRequest') {
    super(t, {statusCode: 400})
  }
}

export class UnauthorizedError extends UserFacingError {
  constructor(t: ErrorID = 'badJWT') {
    super(t, {statusCode: 401})
  }
}

export class ForceLogoutError extends UserFacingError {
  constructor(t: ErrorID = 'badJWT') {
    super(t, {statusCode: 401, forceLogOut: true})
  }
}

export class ForbiddenError extends UserFacingError {
  constructor(t: ErrorID = 'noPermissionError', opts?: UserFacingErrorParams) {
    super(t, {statusCode: 403, ...opts})
  }
}

export class NotFoundError extends UserFacingError {
  constructor(t: ErrorID = '404Text') {
    super(t, {statusCode: 404})
  }
}

export type ClientNetworkErrorParams = UserFacingErrorParams & {
  // True when the request failed before any response arrived, false when the server answered with an
  // error status. `statusCode` can't stand in for it, because it defaults to 500 either way.
  isConnectionFailure?: boolean
  // The server's own error body, when it answered with one a client can read a key off. The error's
  // own `key` is the client-side wrapper's, not the server's.
  responseError?: ErrorForClient
}

export class ClientNetworkError extends UserFacingError {
  public readonly isConnectionFailure?: boolean
  public readonly responseError?: ErrorForClient

  constructor(t: ErrorID = 'serverConnectionErrorTitle', opts?: ClientNetworkErrorParams) {
    super(t, opts)
    this.isConnectionFailure = opts?.isConnectionFailure
    this.responseError = opts?.responseError
  }
}

export class VersionOutdatedError extends UserFacingError {
  constructor(t: 'updateClientError' | 'invalidClientMetadataError', opts?: UserFacingErrorParams) {
    super(t, opts)
  }
}

export class AiDisabledError extends UserFacingError {
  constructor(t: ErrorID = 'aiDisabled', opts?: UserFacingErrorParams) {
    super(t, {statusCode: 422, ...opts})
  }
}
